import React from 'react';

// -- inline icons (lucide-equivalent) --
const S = (p, stroke = 'currentColor') => <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>;
const IcTarget = () => S(<><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>);
const IcZap = () => S(<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />);
const IcEye = () => S(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>);
const IcFilter = () => S(<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />);
const IcClock = () => S(<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>);
const IcBarChart = () => S(<><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></>);
const IcRefresh = () => S(<><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>);
const IcShieldCheck = () => S(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></>);
const IcPlugZap = () => S(<><path d="M6.3 20.3a2.4 2.4 0 0 0 3.4 0L12 18l-6-6-2.3 2.3a2.4 2.4 0 0 0 0 3.4Z" /><path d="m2 22 3-3" /><path d="M7.5 13.5 10 11" /><path d="M10.5 16.5 13 14" /><path d="m18 3-4 4h6l-4 4" /></>);
const IcAlert = () => S(<><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>);
const IcCheck = () => S(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>);
const IcBulb = () => S(<><path d="M9 18h6" /><path d="M10 22h4" /><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z" /></>);

const B1_BULLETS = [
    { Icon: IcTarget, t: 'Context-aware placement.', d: 'Your listing appears in the modules where your service is most relevant — not on a generic marketplace page.' },
    { Icon: IcZap, t: 'Zero cold outreach.', d: 'Users discover you while they’re already working. The introduction is warm because it’s relevant.' },
    { Icon: IcEye, t: 'Tracked click-throughs.', d: 'We track every click from your AppStore card so you know exactly what the platform is generating for you.' },
];
const B2_BULLETS = [
    { Icon: IcFilter, t: 'Pre-qualified by the platform.', d: 'Level 2 partners receive fact-finding checklists completed by the company — so you know their situation, their stage, and their urgency before you pick up the phone.' },
    { Icon: IcClock, t: 'Less time qualifying, more time converting.', d: 'The average B2B sales cycle starts with weeks of qualification. Ours starts with a brief call, because the qualification is already done.' },
    { Icon: IcBarChart, t: 'You choose your lead criteria.', d: 'Set the filters that matter to your business — sector, stage, funding target, geography — and only receive introductions that match.' },
];
const B3_BULLETS = [
    { Icon: IcRefresh, t: 'Live, not batch.', d: 'Records update in real time as companies verify data on the platform — no overnight syncs.' },
    { Icon: IcShieldCheck, t: 'Structured and verified.', d: 'Every field is checked against source documents, not scraped or self-reported.' },
    { Icon: IcPlugZap, t: 'One API, your product.', d: 'Pull the fields you need directly into your own screens — no manual lookups.' },
];

// Reason: class is `bullet` not `row` — a global `.row { display: flex; flex-wrap: wrap }` in rsuite.change.css
// leaked in and wrapped the icon onto its own line above the text.
const Bullets = ({ items }) => (
    <div className="lp-partner-bullets">
        {items.map(({ Icon, t, d }, i) => (
            <div className="bullet" key={i}>
                <span className="ic"><Icon /></span>
                <p className="tx"><span className="t">{t}</span> <span className="d">{d}</span></p>
            </div>
        ))}
    </div>
);

// -- benefit mock cards --
const MockPlacement = () => (
    <div className="lp-partner-mock lp-partner-mock--ip">
        <div className="chip">Business Audit · IP Section</div>
        <div className="flag"><span className="ic"><IcAlert /></span><span>IP not formally assigned to the company</span></div>
        <div className="rec"><span className="ic"><IcBulb /></span>Recommended for this issue:</div>
        <div className="rec-card">
            <div className="top">
                <img src={require('../../../../images/landing-pages/partner/seedlegal.png')} alt="SeedLegals" />
                <span className="tag">Partner</span>
            </div>
            <div className="nm">SeedLegals</div>
            <div className="sub">Transaction documentation &amp; legal services</div>
            <a className="more" href="#cta">More →</a>
        </div>
    </div>
);

const MockLead = () => (
    <div className="lp-partner-mock lp-partner-mock--lead">
        <div className="hd">Incoming lead</div>
        <div className="co">
            <span className="logo">acme</span>
            <div>
                <div className="nm">Acme Tech Ltd</div>
                <div className="sub">Series A · £2M target</div>
            </div>
        </div>
        <div className="need">What they need:</div>
        <div className="need-list">
            {['IP assignment review', 'Employment contracts', 'Shareholder agreement'].map((t, i) => (
                <div key={i}><span className="ic"><IcCheck /></span>{t}</div>
            ))}
        </div>
        <div className="meta">
            <div><span>Diligentsia score:</span><b>Silver 🏅</b></div>
            <div><span>Stage:</span><b>Growth Ready</b></div>
            <div><span>Urgency:</span><b>High<i className="dot" /></b></div>
        </div>
    </div>
);

const MockEnrich = () => (
    <div className="lp-partner-mock lp-partner-mock--enrich">
        <div className="chip">Auto-enriched profile</div>
        <div className="enrich-list">
            {['Company registration', 'Directors & shareholders', 'Filing history', 'Funding rounds'].map((t, i) => (
                <div key={i}><span className="ic"><IcCheck /></span>{t}</div>
            ))}
        </div>
        <div className="synced">Synced via API<i className="dot" /></div>
    </div>
);

const PartnerWhyChooseSection = () => (
    <section className="lp-partner-why">
        <div className="lp-partner-why-inner">
            <h2 className="lp-partner-why-head" data-lp-reveal>Why choose Diligentsia</h2>

            <div className="lp-partner-benefit" data-lp-reveal>
                <div className="copy">
                    <div className="eyebrow">Benefit 1</div>
                    <h3>Not advertising. Placement.</h3>
                    <p>When a company flags an IP gap, they see your IP advisory service. The moment of need and the moment of introduction are the same moment.</p>
                    <Bullets items={B1_BULLETS} />
                </div>
                <div className="art"><MockPlacement /></div>
            </div>

            <div className="lp-partner-benefit is-reverse" data-lp-reveal>
                <div className="copy">
                    <div className="eyebrow">Benefit 2</div>
                    <h3>Leads that already know they need you.</h3>
                    <p>You know what a company needs before the first conversation — because they've already told the platform.</p>
                    <Bullets items={B2_BULLETS} />
                </div>
                <div className="art"><MockLead /></div>
            </div>

            <div className="lp-partner-benefit" data-lp-reveal>
                <div className="copy">
                    <div className="eyebrow">Benefit 3</div>
                    <h3>Your data gets smarter. Your product gets better.</h3>
                    <p>Level 3 partners connect to Diligentsia's structured, verified company data — and auto-enrich their own product with it.</p>
                    <Bullets items={B3_BULLETS} />
                </div>
                <div className="art"><MockEnrich /></div>
            </div>
        </div>
    </section>
);

export default PartnerWhyChooseSection;
